import type { ComponentType } from 'react';
import { Box as ChakraBox } from '@chakra-ui/react';
import { ActivityTooltip } from './ActivityTooltip';
import { ActivityMenuItemIcon } from './ActivityMenuItemIcon';

const Box = ChakraBox as any;

export interface ActivityMenuItem {
  id: string;
  label: string;
  icon?: ComponentType<{ size?: number }>;
  onClick: () => void;
  /**
   * Marks a destructive action (cancel, delete).
   *
   * Deliberately NOT rendered in red. Legacy reds no menu item anywhere — "Cancel
   * interview" sits in the same indigo as its siblings — and the owner confirmed
   * that is what he wants kept. This restyle's scope is the architecture and the
   * kit's chrome, not re-teaching the menu what danger looks like. The flag stays
   * because the SEMANTICS are still worth carrying (a confirmation step hangs off
   * it, and a future design may colour it); it just does not paint anything today.
   */
  destructive?: boolean;
  /**
   * Paints this one item — label and icon together — in a Chakra colour token,
   * held at rest AND while highlighted.
   *
   * Opt-in per item on purpose. The kit's default is that no menu item is
   * coloured (see `destructive` above), and that stays true for every menu that
   * does not ask; internal email's menu is the one design colours, so it names
   * the colours itself rather than the kit changing its mind for everyone.
   */
  color?: string;
  /** Omit the item entirely — gating, not disabling. */
  hidden?: boolean;
  /**
   * Show it, refuse to run it. NOT the same as `hidden`: the coding rows keep
   * "Cancel" visible-but-dead when the platform is disconnected, so the user can
   * see the action exists and that something is wrong. Losing the item entirely
   * would just look like the feature does not exist.
   */
  disabled?: boolean;
  /**
   * Why a disabled item will not run — interview's "An AI coordinator session is
   * already active for this interview." A dead item with no explanation is worse
   * than no item at all.
   */
  tooltip?: string;
}

/**
 * A menu item's label, wrapped so a disabled item can still explain itself.
 *
 * Lives here rather than beside the menus because a module that exports a
 * component must export ONLY components — otherwise Fast Refresh silently stops
 * working for it (`react-refresh/only-export-components`).
 */
export const menuItemLabel = ({
  label,
  tooltip,
  destructive,
  color,
}: ActivityMenuItem) => {
  const content = tooltip ? (
    <ActivityTooltip label={tooltip} data-test-id="activity-disabled-reason">
      <span>{label}</span>
    </ActivityTooltip>
  ) : (
    label
  );

  if (!destructive && !color) return content;

  /**
   * A destructive item reads like every other item — legacy reds none of them.
   *
   * kula's `Menu` paints a `destructive` item `text-error-600` itself, so "do
   * nothing" is not an option — the red has to be actively held back, at rest AND
   * while highlighted (`[data-highlighted] &` beats the hover rule).
   *
   * `color="inherit"` is NOT enough and looked fine for exactly one commit: it
   * inherits from the item, and the item is what kula reddened. It has to name the
   * ordinary colours outright — the same pair `ActivityMenuItemIcon` uses, so the
   * word and its ⊗ move together.
   */
  return (
    <Box
      as="span"
      color={color ?? 'secondary-700'}
      // A coloured item keeps its colour on hover: the darken-to-secondary-900
      // rule exists to stop an uncoloured icon lagging its label, and applying
      // it here would just wash the colour out under the cursor.
      sx={{ '[data-highlighted] &': { color: color ?? 'secondary-900' } }}
      data-test-id="activity-menu-item-label"
    >
      {content}
    </Box>
  );
};

/**
 * Our items → kula `Menu`'s item config.
 *
 * Shared by both menus so a fix lands in one place: `ActivityMoreMenu` and
 * `ActivityMenuAction` had drifted into two copies of this mapping.
 *
 * The separators are not decoration. Figma (7610:23862) rules a 1px `border-color`
 * line under every item *but the last*, and with 5+ items ("Send additional slots",
 * "Edit interview", "Cancel interview", "Mark as no-show", "Assign scorecard") an
 * undivided stack is genuinely hard to read. kula's `Menu` has no per-item border,
 * but it does have a `separator` item type — so interleave one between each pair,
 * which lands in the same place with none of the last-child special-casing.
 */
export const toMenuItems = (items: ActivityMenuItem[]) =>
  items.flatMap((item, index) => [
    ...(index > 0
      ? [{ type: 'separator' as const, key: `${item.id}-separator` }]
      : []),
    {
      key: item.id,
      label: menuItemLabel(item),
      icon: item.icon ? (
        <ActivityMenuItemIcon
          icon={item.icon}
          destructive={item.destructive}
          color={item.color}
        />
      ) : undefined,
      // A disabled item must not run, whatever the menu does with the click.
      onClick: item.disabled ? undefined : item.onClick,
      destructive: item.destructive,
      disabled: item.disabled,
    },
  ]);
